"use client";
import React from 'react';
import { motion } from 'framer-motion';

const CLASH = 'var(--font-clash), "Clash Display", sans-serif';
const MANROPE = 'var(--font-manrope), Manrope, sans-serif';
const INK = '#080400';
const PURPLE = '#3730a3';
const LIME = '#ceff1a';
const CARD = '#fbfdfd';

function SearchGlyph({ size = 17 }: { size?: number }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
            <circle cx="10.5" cy="10.5" r="6.5" stroke={PURPLE} strokeWidth="2" />
            <path d="M15.5 15.5L21 21" stroke={PURPLE} strokeWidth="2" strokeLinecap="round" />
        </svg>
    );
}
function ShoppingGlyph({ size = 17 }: { size?: number }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
            <path d="M5 8h14l-1.4 11H6.4L5 8z" stroke={PURPLE} strokeWidth="2" strokeLinejoin="round" />
            <path d="M8.5 8V6a3.5 3.5 0 0 1 7 0v2" stroke={PURPLE} strokeWidth="2" strokeLinecap="round" />
        </svg>
    );
}
function DisplayGlyph({ size = 17 }: { size?: number }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
            <rect x="3" y="5" width="18" height="12" rx="1.5" stroke={PURPLE} strokeWidth="2" />
            <path d="M8 21h8M12 17v4" stroke={PURPLE} strokeWidth="2" strokeLinecap="round" />
        </svg>
    );
}
function VideoGlyph({ size = 17 }: { size?: number }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
            <rect x="2.5" y="5.5" width="19" height="13" rx="3" stroke={PURPLE} strokeWidth="2" />
            <path d="M10.5 9.5l5 2.5-5 2.5v-5z" fill={PURPLE} />
        </svg>
    );
}
function MailGlyph({ size = 17 }: { size?: number }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
            <rect x="2.5" y="5" width="19" height="14" rx="2" stroke={PURPLE} strokeWidth="2" />
            <path d="M3.5 6.5l8.5 7 8.5-7" stroke={PURPLE} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}
function PinGlyph({ size = 17 }: { size?: number }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
            <path d="M12 22s7-7.5 7-12.5A7 7 0 0 0 5 9.5C5 14.5 12 22 12 22z" stroke={PURPLE} strokeWidth="2" strokeLinejoin="round" />
            <circle cx="12" cy="9.5" r="2.5" stroke={PURPLE} strokeWidth="2" />
        </svg>
    );
}

const orbitNodes = [
    { label: 'Search', angle: -90, Glyph: SearchGlyph },
    { label: 'Shopping', angle: -30, Glyph: ShoppingGlyph },
    { label: 'Display', angle: 30, Glyph: DisplayGlyph },
    { label: 'YouTube', angle: 90, Glyph: VideoGlyph },
    { label: 'Gmail', angle: 150, Glyph: MailGlyph },
    { label: 'Maps', angle: 210, Glyph: PinGlyph },
];

function EcosystemOrbit() {
    return (
        <div className="relative w-full max-w-[380px] aspect-square mx-auto">
            {/* slow-rotating orbit rings */}
            <motion.svg
                className="absolute inset-0 w-full h-full"
                viewBox="0 0 100 100"
                animate={{ rotate: 360 }}
                transition={{ duration: 60, repeat: Infinity, ease: 'linear' }}
            >
                <circle cx="50" cy="50" r="42" fill="none" stroke={PURPLE} strokeOpacity="0.22" strokeWidth="0.6" strokeDasharray="1 4.5" strokeLinecap="round" />
                <circle cx="50" cy="50" r="30" fill="none" stroke={PURPLE} strokeOpacity="0.14" strokeWidth="0.6" strokeDasharray="1 4.5" strokeLinecap="round" />
            </motion.svg>

            {/* soft pulsing glow behind the hub */}
            <motion.div
                className="absolute rounded-full pointer-events-none"
                style={{ left: '50%', top: '50%', width: 140, height: 140, marginLeft: -70, marginTop: -70, background: 'radial-gradient(circle, rgba(55,48,163,0.22) 0%, transparent 70%)' }}
                animate={{ scale: [1, 1.15, 1], opacity: [0.6, 1, 0.6] }}
                transition={{ duration: 3.2, repeat: Infinity, ease: 'easeInOut' }}
            />

            {/* center hub */}
            <motion.div
                initial={{ opacity: 0, scale: 0.7 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, ease: 'backOut' }}
                className="absolute rounded-full flex items-center justify-center text-center"
                style={{ left: '50%', top: '50%', width: 92, height: 92, marginLeft: -46, marginTop: -46, background: INK, boxShadow: '0 16px 40px -14px rgba(8,4,0,0.4)' }}
            >
                <span className="text-[11px] font-bold uppercase leading-tight" style={{ fontFamily: CLASH, color: LIME, letterSpacing: '0.02em' }}>
                    Google<br />Ads
                </span>
            </motion.div>

            {/* orbiting channel nodes */}
            {orbitNodes.map((n, i) => {
                const rad = (n.angle * Math.PI) / 180;
                const rPct = 40;
                const left = 50 + rPct * Math.cos(rad);
                const top = 50 + rPct * Math.sin(rad);
                return (
                    <div
                        key={n.label}
                        className="absolute"
                        style={{ left: `${left}%`, top: `${top}%`, transform: 'translate(-50%, -50%)' }}
                    >
                        <motion.div
                            initial={{ opacity: 0, scale: 0.5 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.45, delay: 0.25 + i * 0.09, ease: 'backOut' }}
                            className="flex flex-col items-center gap-1.5"
                        >
                            <span
                                className="w-11 h-11 rounded-full flex items-center justify-center"
                                style={{ background: CARD, border: '1px solid rgba(55,48,163,0.18)', boxShadow: '0 10px 22px -10px rgba(8,4,0,0.2)' }}
                            >
                                <n.Glyph size={17} />
                            </span>
                            <span className="text-[10px] font-semibold whitespace-nowrap" style={{ fontFamily: MANROPE, color: 'rgba(8,4,0,0.5)' }}>
                                {n.label}
                            </span>
                        </motion.div>
                    </div>
                );
            })}
        </div>
    );
}

export default function GoogleAdsWhatIs() {
    return (
        <section className="relative overflow-hidden pt-20 pb-16 md:pt-28 md:pb-20 px-6 md:px-16 lg:px-24" style={{ background: '#ffffff', borderTopLeftRadius: '80px', borderTopRightRadius: '80px' }}>
            <div className="absolute inset-0 pointer-events-none opacity-[0.035]" style={{ backgroundImage: 'radial-gradient(circle, rgba(55,48,163,1) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />

            <div className="max-w-[1200px] mx-auto relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
                <div className="lg:col-span-7 flex flex-col gap-6">
                    <motion.span
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="text-xs font-bold tracking-[0.2em] uppercase"
                        style={{ color: '#3730a3', fontFamily: CLASH }}
                    >
                        The Basics
                    </motion.span>

                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        className="text-3xl md:text-5xl font-bold tracking-tight leading-tight"
                        style={{ fontFamily: CLASH, color: '#080400' }}
                    >
                        What Is Google Ads Management?
                    </motion.h2>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="flex flex-col gap-5"
                    >
                        <p
                            className="text-base md:text-lg leading-relaxed"
                            style={{ fontFamily: MANROPE, color: 'rgba(8,4,0,0.6)', letterSpacing: '0.02em' }}
                        >
                            Google Ads management is the ongoing process of planning, building, monitoring and optimising paid campaigns across Google&apos;s advertising ecosystem.
                        </p>
                        <p
                            className="text-base md:text-lg leading-relaxed"
                            style={{ fontFamily: MANROPE, color: 'rgba(8,4,0,0.6)', letterSpacing: '0.02em' }}
                        >
                            Effective management goes beyond choosing keywords and increasing budgets. It involves understanding what people are searching for, why they&apos;re searching, what happens after the click and whether that journey ultimately creates business value.
                        </p>
                    </motion.div>
                </div>

                <div className="lg:col-span-5">
                    <EcosystemOrbit />
                </div>
            </div>
        </section>
    );
}
